import {selectHexes, selectSpecialBuildings} from "../redux/reducers/sharedStateReducer/selectors";
import {calculateCitiesCache} from "../utils/gameState/calculateCitiesCache";
import {CENTRAL_QUARTER_PREFIX} from "../constants/tokens";
import {playCityGrowthAnimation, playStartingQuarterCityGrowthAnimation} from "./playCityGrowthAnimation";
import {
  checkIfBuildingInPlan,
  checkIfBuildingPlannedOnHex,
  checkIfSpecialBuildingJustBuilt,
  checkIfSpecialBuildingJustPlaced,
  checkIfStartingQuarterJustPlaced,
  playBuildingDustAnimation,
  playPlannedBuildingDustAnimation,
  playSpecialBuildingDustAnimation,
  playStartingQuarterDustAnimation,
} from "./playBuildingDustAnimation";
import {checkIfSpecialBuilding} from "../utils/buildings/checkIfSpecialBuilding";
import {STARTING_QUARTERS_REVEAL_STAGGER_MS} from "../configs/buildingConstructionAnimation";
import {playBuildingDropAnimation} from "./playBuildingDropAnimation";
import {playNearbyGuildsTemplesIncomeChangeAnimation} from "./playNearbyGuildsTemplesIncomeChangeAnimation";
import {
  playFarmSupplyNetworkAnimation,
  playPlannedFarmSupplyNetworkAnimation,
} from "./playFarmSupplyNetworkAnimation";
import {createLandingCoinsSpentAnimation, playCoinsSpentAnimation} from "./playCoinsSpentAnimation";
import {holdQuarterZoneBurn, resolveBuildingImpact, showBuildingImpact} from "./playBuildingImpactEffects";
import {
  buildStateBeforePlacement,
  buildStateBeforePlanEntry,
  buildStateBeforeStartingQuarter,
} from "../utils/gameState/construction/resolveNewQuarterZoneCells";

/**
 * Разовые анимации размещения жетона (SET_HEX_TOKEN): рост города, которому
 * досталась ячейка, доход соседних гильдий/храмов, на который это размещение
 * повлияло, и — для фермы — сеть ее связей снабжения с остальными фермами
 * владельца. Все запускаются на каждое размещение — у строящего из GameScene,
 * у остальных участников матча из playEffectForServerAction.
 *
 * Обе анимации опираются на одну и ту же пару кэшей городов «до/после»,
 * посчитанную заново из hexes (кэш стора обновляется только на COMPLETE_PHASE
 * и в течение хода строительства устарел). Считаем ее здесь один раз: каждый
 * calculateCitiesCache — полный обход поля, а на одном размещении их и без
 * того несколько (updateHexes подписчика стора, маркеры доступности).
 *
 * @param {Object} store
 * @param {Object} soundManager - без него доход соседей не озвучивается и не
 *   показывается, рост города играет без звука захвата рудников и руин
 * @param {Object} hexGridRenderer
 * @param {string} placedHexKey
 * @param {Object} hexesBefore - hexes ДО диспатча размещения
 * @param {Array} [planEntriesBefore] - план строительства этого клиента ДО
 *   диспатча: чужая стройка могла снять его часть (см.
 *   getPlannedGuildsAndTemplesIncomeChange)
 * @param {Object} [options]
 * @param {Function} [options.playPlacementSound] - звук постройки. Зовется в
 *   момент, когда жетон коснулся земли, а не при вызове: жетон прилетает
 *   сверху (см. playBuildingDropAnimation). Без поля играет сразу
 * @param {number} [options.price] - сколько монет стоила постройка: столько
 *   всплывает над ячейкой на приземлении жетона (см. playCoinsSpentAnimation)
 * @param {boolean} [options.isPaced] - постройка чужая (бот, соперник по сети,
 *   запись матча)
 */
export const playBuildingPlacementEffects = (
  store, soundManager, hexGridRenderer, placedHexKey, hexesBefore, planEntriesBefore,
  {playPlacementSound, price, isPaced} = {}
) => {
  if (!hexGridRenderer || !hexesBefore) {
    // Анимаций нет, но звук постройки игрок услышать должен
    playPlacementSound?.();
    return;
  }

  const state = store.getState();
  const specialBuildings = selectSpecialBuildings(state);
  const citiesCaches = {
    citiesCacheBefore: calculateCitiesCache(hexesBefore, specialBuildings),
    citiesCacheAfter: calculateCitiesCache(selectHexes(state), specialBuildings),
  };

  // Жетон прилетает сверху, и только в момент удара о землю звучит постройка
  // и поднимается пыль. Ни того, ни другого не будет, если жетон на ячейке не
  // изменился (воронку зовут и на отклоненных редьюсером действиях)
  const placedToken = selectHexes(state)?.[placedHexKey];
  const isTokenPlaced = placedToken !== hexesBefore[placedHexKey];
  const showCoinsSpent = createLandingCoinsSpentAnimation(
    store, hexGridRenderer, {hexKey: placedHexKey, amount: price, isPaced}
  );
  const impact = isTokenPlaced && placedToken ? resolveBuildingImpact({tokenType: placedToken}) : null;
  const zoneBurn = isTokenPlaced
    ? holdQuarterZoneBurn(store, hexGridRenderer, placedHexKey, placedToken, (stateNow) => (
      buildStateBeforePlacement(stateNow, placedHexKey, hexesBefore)
    ))
    : null;
  playBuildingDropAnimation(store, hexGridRenderer, placedHexKey, {
    tokenType: isTokenPlaced ? placedToken : null,
    impact,
    onLand: () => {
      const tokenNow = selectHexes(store.getState())?.[placedHexKey];
      // Постройку могли отменить, пока жетон летел: звук стройки после звука
      // отмены — та же ошибка, что и пыль на пустой ячейке
      if (tokenNow === placedToken) {
        playPlacementSound?.();
        playBuildingDustAnimation(store, hexGridRenderer, placedHexKey, hexesBefore);
        if (impact) {
          showBuildingImpact(hexGridRenderer, placedHexKey, impact);
        }
        zoneBurn?.ignite();
      } else {
        zoneBurn?.release();
      }
      // Число монет — только за состоявшуюся постройку: на отклоненном действии
      // жетон не изменился, а отмененная вернула ячейке прежний — монеты не
      // списаны. Если же ячейка успела получить другой жетон (редкость: за ход
      // на ячейке строят один раз, но действия двух ходов могут прийти пачкой),
      // полет оборван, а монеты за эту постройку списаны — число показываем
      if (isTokenPlaced && tokenNow !== hexesBefore[placedHexKey]) {
        showCoinsSpent();
      }
    },
  });
  playCityGrowthAnimation(store, hexGridRenderer, placedHexKey, hexesBefore, citiesCaches, soundManager);
  playNearbyGuildsTemplesIncomeChangeAnimation(
    store, soundManager, hexGridRenderer, placedHexKey, hexesBefore, citiesCaches, planEntriesBefore
  );
  playFarmSupplyNetworkAnimation(store, hexGridRenderer, placedHexKey, hexesBefore, citiesCaches);
};

/**
 * То же для специальных строений (SET_SPECIAL_BUILDING): они ставятся своим
 * действием мимо общей воронки — редьюсер пишет в specialBuildings, ячейки
 * hexes не меняются, поэтому ни рост города, ни доход соседей тут не считаются.
 * Остаются прилет жетона, звук постройки и пыль в момент приземления
 *
 * @param {Object} store
 * @param {Object} hexGridRenderer
 * @param {string} hexKey
 * @param {string} buildingType
 * @param {Object} [options]
 * @param {Object} [options.specialBuildingsBefore] - срез ДО диспатча: есть у
 *   местного игрока (его действие редьюсер мог отклонить) и не нужен для
 *   действий с сервера — те уже применены
 * @param {Function} [options.playPlacementSound] - звук постройки в момент
 *   приземления жетона
 * @param {number} [options.price] - сколько монет стоило строение: столько
 *   всплывает над ячейкой на приземлении жетона
 * @param {boolean} [options.isPaced] - постройка чужая (бот, соперник по сети,
 *   запись матча)
 */
export const playSpecialBuildingPlacementEffects = (
  store, hexGridRenderer, hexKey, buildingType, {specialBuildingsBefore, playPlacementSound, price, isPaced} = {}
) => {
  if (!hexGridRenderer || !checkIfSpecialBuildingJustPlaced(store, hexKey, buildingType, specialBuildingsBefore)) {
    playPlacementSound?.();
    // Крепость, скрытая переключателем показа, не прилетает, но построена и
    // оплачена: число всплывает сразу, вместе со звуком монет
    if (checkIfSpecialBuildingJustBuilt(store, hexKey, buildingType, specialBuildingsBefore)) {
      playCoinsSpentAnimation(store, hexGridRenderer, {hexKey, amount: price, isPaced});
    }
    return;
  }

  const showCoinsSpent = createLandingCoinsSpentAnimation(store, hexGridRenderer, {hexKey, amount: price, isPaced});
  const impact = resolveBuildingImpact({specialBuildingType: buildingType});
  playBuildingDropAnimation(store, hexGridRenderer, hexKey, {
    specialBuildingType: buildingType,
    impact,
    onLand: () => {
      // Строение могли снять, пока жетон летел (двойной клик по ячейке)
      if (!checkIfSpecialBuildingJustPlaced(store, hexKey, buildingType)) {
        return;
      }
      playPlacementSound?.();
      playSpecialBuildingDustAnimation(store, hexGridRenderer, hexKey, buildingType, specialBuildingsBefore);
      showBuildingImpact(hexGridRenderer, hexKey, impact);
      showCoinsSpent();
    },
  });
};

/**
 * То же для жетона, поставленного в ПЛАН строительства (игрок размечает свой
 * будущий ход, пока идет чужой ход фазы строительства): прилет сверху, звук
 * постройки и пыль в момент приземления — как у настоящей стройки в свой ход.
 *
 * Запланированного жетона нет ни в hexes, ни в specialBuildings: он лежит в
 * плане, и все сверки идут по плану (checkIfBuildingPlannedOnHex). Доход
 * гильдий и храмов у плана показывает своя анимация
 * (playPlanPlacementIncomeAnimations), здесь ее нет. А сеть связей снабжения
 * запланированной фермы — здесь, как и у настоящей постройки.
 *
 * Число монет всплывает уже здесь, при планировании: на столько в этот момент
 * уменьшается баланс игрока в шапке. Когда план применится в начале хода,
 * своих эффектов у владельца плана нет (см. constructionPlanMiddleware), и
 * второй раз число не покажется; остальные игроки плана не видят и увидят
 * число у настоящей постройки
 *
 * @param {Object} store
 * @param {Object} hexGridRenderer
 * @param {string} hexKey
 * @param {string} token — запланированный жетон (обычный или спецстроение)
 * @param {Object} [options]
 * @param {Function} [options.playPlacementSound] - звук постройки в момент
 *   приземления жетона
 * @param {number} [options.price] - во сколько монет обойдется запланированное
 *   строение: столько всплывает над ячейкой на приземлении жетона
 */
export const playPlannedBuildingPlacementEffects = (store, hexGridRenderer, hexKey, token, {
  playPlacementSound,
  price,
} = {}) => {
  if (!hexGridRenderer || !checkIfBuildingPlannedOnHex(store, hexKey, token)) {
    playPlacementSound?.();
    // Запланированную крепость при скрытом слое крепостей план не рисует, но
    // баланс она уменьшила: число всплывает сразу, вместе со звуком монет
    if (checkIfBuildingInPlan(store, hexKey, token)) {
      playCoinsSpentAnimation(store, hexGridRenderer, {hexKey, amount: price});
    }
    return;
  }

  const isSpecialBuilding = checkIfSpecialBuilding(token);
  const showCoinsSpent = createLandingCoinsSpentAnimation(store, hexGridRenderer, {hexKey, amount: price});
  const impact = resolveBuildingImpact(isSpecialBuilding ? {specialBuildingType: token} : {tokenType: token});
  const zoneBurn = holdQuarterZoneBurn(store, hexGridRenderer, hexKey, token, (stateNow) => (
    buildStateBeforePlanEntry(stateNow, hexKey, token)
  ));
  playBuildingDropAnimation(store, hexGridRenderer, hexKey, {
    tokenType: isSpecialBuilding ? null : token,
    specialBuildingType: isSpecialBuilding ? token : null,
    isPlanned: true,
    impact,
    onLand: () => {
      // Запланированное могли снять, пока жетон летел (двойной клик по ячейке)
      if (!checkIfBuildingPlannedOnHex(store, hexKey, token)) {
        zoneBurn.release();
        return;
      }
      playPlacementSound?.();
      playPlannedBuildingDustAnimation(store, hexGridRenderer, hexKey, token);
      showBuildingImpact(hexGridRenderer, hexKey, impact);
      zoneBurn.ignite();
      showCoinsSpent();
    },
  });
  playPlannedFarmSupplyNetworkAnimation(store, hexGridRenderer, hexKey, token);
};

/**
 * То же для стартового квартала, который игрок выбирает в фазе setup: у него
 * свой путь — редьюсер пишет в startingQuarters, а на поле квартал рисуется
 * накладкой поверх флага своей ячейки. Прилет, звук и пыль — как у построек,
 * рост города с доходом рудников и руин — как у центрального квартала
 *
 * @param {Object} store
 * @param {Object} hexGridRenderer
 * @param {string} hexKey
 * @param {Object} params
 * @param {string} params.playerId — чей стартовый квартал
 * @param {string} params.color — цвет игрока: от него зависит жетон квартала
 * @param {Function} [params.playPlacementSound] - звук в момент приземления
 * @param {Object} [params.soundManager] - звук захвата рудников и руин; без
 *   него рост города играет молча
 */
export const playStartingQuarterPlacementEffects = (store, hexGridRenderer, hexKey, {
  playerId,
  color,
  playPlacementSound,
  soundManager,
} = {}) => {
  if (!hexGridRenderer || !checkIfStartingQuarterJustPlaced(store, hexKey, playerId)) {
    playPlacementSound?.();
    return;
  }

  const token = `${CENTRAL_QUARTER_PREFIX}${color}`;
  const impact = resolveBuildingImpact({tokenType: token});
  const zoneBurn = holdQuarterZoneBurn(store, hexGridRenderer, hexKey, token, (stateNow) => (
    buildStateBeforeStartingQuarter(stateNow, playerId)
  ));
  playBuildingDropAnimation(store, hexGridRenderer, hexKey, {
    tokenType: token,
    isStartingQuarter: true,
    impact,
    onLand: () => {
      // Выбор могли поменять, пока квартал летел: тогда ни звука, ни пыли
      if (!checkIfStartingQuarterJustPlaced(store, hexKey, playerId)) {
        zoneBurn.release();
        return;
      }
      playPlacementSound?.();
      playStartingQuarterDustAnimation(store, hexGridRenderer, hexKey, playerId);
      showBuildingImpact(hexGridRenderer, hexKey, impact);
      zoneBurn.ignite();
    },
  });
  playStartingQuarterCityGrowthAnimation(store, hexGridRenderer, soundManager, hexKey, {playerId, color});
};

/**
 * Расстановка закончилась, и стало видно, где стоят остальные: их центральные кварталы
 * появляются так же, как настоящая постройка центрального квартала (см.
 * playBuildingPlacementEffects), по очереди. Все они уже лежат в hexes разом, поэтому
 * для каждого «до» — то же поле без него одного: зона и город считаются только за него.
 *
 * Кто ждет своей очереди, придержан (HexGridRenderer.holdBuildingDrop), иначе стоял бы
 * на поле до собственного падения.
 *
 * @param {Object} store
 * @param {Object} soundManager
 * @param {Object} hexGridRenderer
 * @param {Array<{hexKey: string, tokenType: string}>} quarters — см.
 *   resolveRevealedStartingQuarters
 * @param {function(function(): void, number): void} schedule — отложить вызов на столько
 *   мс: таймер должен погибнуть вместе со сценой
 */
export const playStartingQuartersRevealEffects = (store, soundManager, hexGridRenderer, quarters, schedule) => {
  if (!hexGridRenderer) {
    return;
  }

  const playQuarter = ({hexKey, tokenType}, index) => {
    const hexes = selectHexes(store.getState());
    if (hexes[hexKey] !== tokenType) {
      hexGridRenderer.revealHexTokenSprites(hexKey);
      return;
    }
    const {[hexKey]: _placed, ...hexesBefore} = hexes;
    playBuildingPlacementEffects(store, soundManager, hexGridRenderer, hexKey, hexesBefore, undefined, {
      playPlacementSound: () => soundManager?.playStartingQuarterReveal({isHornPlayed: index === 0}),
      isPaced: true,
    });
  };

  quarters.forEach(({hexKey}) => hexGridRenderer.holdBuildingDrop(hexKey));
  quarters.forEach((quarter, index) => {
    if (index === 0) {
      playQuarter(quarter, index);
    } else {
      schedule(() => playQuarter(quarter, index), index * STARTING_QUARTERS_REVEAL_STAGGER_MS);
    }
  });
};
